<template>
  <div class="story-reader" :class="{ 'fullscreen': isFullscreen }">
    <!-- Reader Header -->
    <div class="reader-header">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <button class="btn btn-ghost me-3" @click="closeReader">
            <i class="bi bi-arrow-left"></i> Back to Library
          </button>
          <div>
            <h5 class="mb-0 fw-bold">{{ currentBook.title }}</h5>
            <small class="text-muted">by {{ currentBook.author }}</small>
          </div>
        </div>
        
        <div class="reader-controls d-flex align-items-center gap-2">
          <button class="btn btn-ghost" @click="toggleBookmark" :class="{ 'text-warning': isBookmarked }">
            <i class="bi bi-bookmark-fill"></i>
          </button>
          <button class="btn btn-ghost" @click="toggleTextToSpeech" :class="{ 'text-primary': isSpeaking }">
            <i class="bi bi-volume-up"></i>
          </button>
          <button class="btn btn-ghost" @click="toggleFullscreen">
            <i class="bi bi-fullscreen"></i>
          </button>
          <div class="reading-progress-mini">
            <span class="progress-text">{{ currentPageIndex + 1 }} / {{ totalPages }}</span>
            <div class="progress-bar-mini">
              <div class="progress-fill-mini" :style="{ width: readingProgressPercentage + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Story Content -->
    <div class="story-content" ref="storyContent">
      <div class="story-page" v-if="currentPage">
        <!-- Page Header -->
        <div class="page-header text-center mb-4">
          <div class="page-illustration">{{ currentPage.illustration }}</div>
          <h3 class="page-title">{{ currentPage.title }}</h3>
        </div>

        <!-- Page Text -->
        <div class="page-text" :class="{ 'large-text': isLargeText }">
          <p>{{ currentPage.text }}</p>
        </div>

        <!-- Interactive Elements -->
        <div v-if="currentPage.questions" class="page-questions mt-4">
          <h6 class="fw-bold mb-3">Think About It:</h6>
          <div v-for="(question, index) in currentPage.questions" :key="index" class="question-item">
            <p class="question-text">{{ question.text }}</p>
            <div v-if="question.type === 'multiple-choice'" class="answer-options">
              <button 
                v-for="(option, optIndex) in question.options" 
                :key="optIndex"
                class="btn btn-outline-primary me-2 mb-2"
                @click="selectAnswer(question.id, option)"
                :class="{ 'btn-primary': selectedAnswers[question.id] === option }"
              >
                {{ option }}
              </button>
            </div>
            <div v-else class="answer-input">
              <textarea 
                v-model="selectedAnswers[question.id]"
                class="form-control"
                rows="3"
                :placeholder="question.placeholder || 'Write your thoughts here...'"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Vocabulary Highlights -->
        <div v-if="currentPage.vocabulary" class="vocabulary-section mt-4">
          <h6 class="fw-bold mb-3">New Words:</h6>
          <div class="vocabulary-grid">
            <div 
              v-for="word in currentPage.vocabulary" 
              :key="word.term"
              class="vocabulary-card"
              @click="showDefinition(word)"
            >
              <strong>{{ word.term }}</strong>
              <small class="text-muted d-block">{{ word.definition }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Controls -->
    <div class="story-navigation">
      <div class="d-flex align-items-center justify-content-between">
        <button 
          class="btn btn-primary btn-lg"
          @click="previousPage"
          :disabled="currentPageIndex === 0"
        >
          <i class="bi bi-chevron-left"></i> Previous
        </button>

        <!-- Page Indicators -->
        <div class="page-indicators">
          <span 
            v-for="(page, index) in currentBook.content" 
            :key="index"
            class="page-dot"
            :class="{ 
              'active': index === currentPageIndex,
              'completed': index < currentPageIndex
            }"
            @click="goToPage(index)"
          ></span>
        </div>

        <button 
          class="btn btn-primary btn-lg"
          @click="nextPage"
          :disabled="currentPageIndex === totalPages - 1"
        >
          Next <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Reading Completion Modal -->
    <div v-if="showCompletionModal" class="modal-overlay" @click="closeCompletionModal">
      <div class="modal-content completion-modal" @click.stop>
        <div class="modal-header text-center">
          <div class="completion-celebration">
            <div class="celebration-icon">🎉</div>
            <h4 class="fw-bold text-success">Story Complete!</h4>
            <p class="mb-0">You finished "{{ currentBook.title }}"</p>
          </div>
        </div>
        <div class="modal-body text-center">
          <div class="reading-stats">
            <div class="row">
              <div class="col-4">
                <div class="stat-item">
                  <div class="stat-number">{{ readingTimeMinutes }}</div>
                  <div class="stat-label">Minutes</div>
                </div>
              </div>
              <div class="col-4">
                <div class="stat-item">
                  <div class="stat-number">{{ totalPages }}</div>
                  <div class="stat-label">Pages</div>
                </div>
              </div>
              <div class="col-4">
                <div class="stat-item">
                  <div class="stat-number">{{ wordsRead }}</div>
                  <div class="stat-label">Words</div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="completion-actions mt-4">
            <button class="btn btn-success me-2" @click="rateStory">
              <i class="bi bi-star me-2"></i>Rate Story
            </button>
            <button class="btn btn-primary me-2" @click="findSimilarBooks">
              <i class="bi bi-book me-2"></i>Find Similar
            </button>
            <button class="btn btn-outline-primary" @click="shareProgress">
              <i class="bi bi-share me-2"></i>Share
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeCompletionModal">Close</button>
          <button class="btn btn-primary" @click="backToLibrary">Back to Library</button>
        </div>
      </div>
    </div>

    <!-- Definition Tooltip -->
    <div v-if="showDefinitionTooltip" class="definition-tooltip" :style="tooltipStyle">
      <div class="tooltip-content">
        <h6 class="fw-bold">{{ selectedWord.term }}</h6>
        <p class="mb-0">{{ selectedWord.definition }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StoryReader',
  props: {
    book: {
      type: Object,
      required: true
    },
    startPage: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      currentPageIndex: this.startPage,
      isFullscreen: false,
      isBookmarked: false,
      isSpeaking: false,
      isLargeText: false,
      selectedAnswers: {},
      readingStartTime: Date.now(),
      showCompletionModal: false,
      showDefinitionTooltip: false,
      selectedWord: {},
      tooltipStyle: {},
      speechSynthesis: null,
      currentUtterance: null
    }
  },
  computed: {
    currentBook() {
      return this.book;
    },
    currentPage() {
      return this.currentBook.content[this.currentPageIndex];
    },
    totalPages() {
      return this.currentBook.content.length;
    },
    readingProgressPercentage() {
      return Math.round(((this.currentPageIndex + 1) / this.totalPages) * 100);
    },
    readingTimeMinutes() {
      return Math.round((Date.now() - this.readingStartTime) / 60000);
    },
    wordsRead() {
      let totalWords = 0;
      for (let i = 0; i <= this.currentPageIndex; i++) {
        const page = (this.currentBook && Array.isArray(this.currentBook.content)) ? this.currentBook.content[i] : null
        if (page && typeof page.text === 'string') {
          totalWords += page.text.split(/\s+/).filter(Boolean).length
        }
      }
      return totalWords;
    }
  },
  mounted() {
    this.initializeSpeechSynthesis();
    this.loadBookmarks();
    this.trackReadingProgress();
  },
  beforeUnmount() {
    this.stopSpeaking();
    this.saveReadingProgress();
  },
  methods: {
    // Navigation methods
    nextPage() {
      if (this.currentPageIndex < this.totalPages - 1) {
        this.currentPageIndex++;
        this.saveReadingProgress();
        if (this.currentPageIndex === this.totalPages - 1) {
          setTimeout(() => {
            this.showCompletionModal = true;
          }, 1000);
        }
      }
    },
    
    previousPage() {
      if (this.currentPageIndex > 0) {
        this.currentPageIndex--;
        this.saveReadingProgress();
      }
    },
    
    goToPage(pageIndex) {
      this.currentPageIndex = pageIndex;
      this.saveReadingProgress();
    },
    
    // Reader controls
    closeReader() {
      this.saveReadingProgress();
      this.$emit('close');
    },
    
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },
    
    toggleBookmark() {
      this.isBookmarked = !this.isBookmarked;
      this.saveBookmark();
    },
    
    toggleTextToSpeech() {
      if (this.isSpeaking) {
        this.stopSpeaking();
      } else {
        this.startSpeaking();
      }
    },
    
    // Text-to-speech functionality
    initializeSpeechSynthesis() {
      if ('speechSynthesis' in window) {
        this.speechSynthesis = window.speechSynthesis;
      }
    },
    
    startSpeaking() {
      if (this.speechSynthesis && this.currentPage) {
        this.stopSpeaking();
        this.currentUtterance = new SpeechSynthesisUtterance(this.currentPage.text);
        this.currentUtterance.rate = 0.8;
        this.currentUtterance.pitch = 1;
        this.currentUtterance.onend = () => {
          this.isSpeaking = false;
        };
        this.speechSynthesis.speak(this.currentUtterance);
        this.isSpeaking = true;
      }
    },
    
    stopSpeaking() {
      if (this.speechSynthesis) {
        this.speechSynthesis.cancel();
        this.isSpeaking = false;
      }
    },
    
    // Interactive features
    selectAnswer(questionId, answer) {
      // Vue 3: replace $set with direct assignment
      this.selectedAnswers = { ...this.selectedAnswers, [questionId]: answer }
    },
    
    showDefinition(word) {
      this.selectedWord = word;
      this.showDefinitionTooltip = true;
      // Position tooltip (simplified)
      this.tooltipStyle = {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
      
      setTimeout(() => {
        this.showDefinitionTooltip = false;
      }, 3000);
    },
    
    // Completion actions
    closeCompletionModal() {
      this.showCompletionModal = false;
    },
    
    rateStory() {
      // Implement rating functionality
      this.showFeedback('Thanks for rating this story!', 'success');
      this.closeCompletionModal();
    },
    
    findSimilarBooks() {
      this.closeCompletionModal();
      this.$emit('find-similar', this.currentBook);
    },
    
    shareProgress() {
      this.showFeedback('Reading progress shared!', 'success');
    },
    
    backToLibrary() {
      this.closeCompletionModal();
      this.closeReader();
    },
    
    // Data persistence
    async saveReadingProgress() {
      const progressData = {
        bookId: this.currentBook.id,
        currentPage: this.currentPageIndex,
        lastRead: new Date().toISOString(),
        readingTime: Date.now() - this.readingStartTime
      };
      const savedProgress = JSON.parse(localStorage.getItem('readingProgress') || '{}');
      savedProgress[this.currentBook.id] = progressData;
      localStorage.setItem('readingProgress', JSON.stringify(savedProgress));

      // Persist to backend for analytics/badges
      try {
        const raw = localStorage.getItem('currentUser')
        const user = raw ? JSON.parse(raw) : null
        const userId = user?.id
        if (!userId) return

        const isLastPage = this.currentPageIndex + 1 >= this.totalPages
        const payload = { most_recent_pg: this.currentPageIndex + 1, favourite: this.isBookmarked ? 1 : 0 }
        await (await import('../services/readingService.js')).updateProgress(userId, this.currentBook.id, payload)
        // Backend awards Reading Rookie when completed
        if (isLastPage) {
          // no-op here; backend already handles points and badges
        }
      } catch (e) {
        // ignore; keep local progress
      }
    },
    
    loadBookmarks() {
      const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '[]');
      this.isBookmarked = bookmarks.includes(this.currentBook.id);
    },
    
    saveBookmark() {
      let bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '[]');
      if (this.isBookmarked) {
        if (!bookmarks.includes(this.currentBook.id)) {
          bookmarks.push(this.currentBook.id);
        }
      } else {
        bookmarks = bookmarks.filter(id => id !== this.currentBook.id);
      }
      localStorage.setItem('bookmarks', JSON.stringify(bookmarks));
    },
    
    trackReadingProgress() {
      // Update reading streak
      const today = new Date().toDateString();
      const readingStreak = JSON.parse(localStorage.getItem('readingStreak') || '{"days": [], "current": 0}');
      
      if (!readingStreak.days.includes(today)) {
        readingStreak.days.push(today);
        readingStreak.current = readingStreak.days.length;
        localStorage.setItem('readingStreak', JSON.stringify(readingStreak));
      }
      
      // Track reading session
      const sessionData = {
        bookId: this.currentBook.id,
        startTime: this.readingStartTime,
        pagesRead: this.currentPageIndex + 1
      };
      
      // Store session data for analytics
      const sessions = JSON.parse(localStorage.getItem('readingSessions') || '[]');
      sessions.push(sessionData);
      localStorage.setItem('readingSessions', JSON.stringify(sessions));
    },
    
    showFeedback(message, type) {
      const alertClass = type === 'success' ? 'alert-success' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 350px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      
      document.body.appendChild(alert);
      setTimeout(() => {
        if (alert.parentNode) {
          alert.parentNode.removeChild(alert);
        }
      }, 4000);
    }
  }
}
</script>

<style scoped>
.story-reader {
  min-height: 100vh;
  background: var(--bg-primary);
  display: flex;
  flex-direction: column;
}

.story-reader.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: var(--bg-primary);
}

.reader-header {
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  padding: 15px 20px;
  position: sticky;
  top: 0;
  z-index: 100;
}

.btn-ghost {
  background: transparent;
  border: none;
  color: var(--text-primary);
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-ghost:hover {
  background: var(--bg-tertiary);
  color: var(--role-primary);
}

.reading-progress-mini {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg-primary);
  padding: 8px 12px;
  border-radius: 20px;
  border: 1px solid var(--border-color);
}

.progress-bar-mini {
  width: 60px;
  height: 4px;
  background: var(--border-color);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill-mini {
  height: 100%;
  background: var(--role-primary);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
}

.story-content {
  flex: 1;
  padding: 40px 20px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.story-page {
  background: var(--bg-primary);
  border-radius: 15px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  min-height: 500px;
}

.page-header {
  margin-bottom: 30px;
}

.page-illustration {
  font-size: 80px;
  margin-bottom: 20px;
}

.page-title {
  color: var(--text-primary);
  font-size: 24px;
  margin-bottom: 20px;
}

.page-text {
  font-size: 18px;
  line-height: 1.8;
  color: var(--text-primary);
  text-align: justify;
}

.page-text.large-text {
  font-size: 22px;
  line-height: 2;
}

.page-questions {
  background: var(--bg-secondary);
  border-radius: 15px;
  padding: 20px;
  border: 1px solid var(--border-color);
}

.question-item {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-color);
}

.question-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.question-text {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.vocabulary-section {
  background: var(--bg-secondary);
  border-radius: 15px;
  padding: 20px;
  border: 1px solid var(--border-color);
}

.vocabulary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.vocabulary-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.vocabulary-card:hover {
  border-color: var(--role-primary);
  transform: translateY(-2px);
}

.story-navigation {
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  padding: 20px;
  position: sticky;
  bottom: 0;
}

.page-indicators {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--border-color);
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-dot.completed {
  background: var(--role-success);
}

.page-dot.active {
  background: var(--role-primary);
  transform: scale(1.3);
}

.page-dot:hover {
  transform: scale(1.2);
}

.completion-modal {
  max-width: 500px;
}

.completion-celebration {
  text-align: center;
  padding: 20px;
}

.celebration-icon {
  font-size: 64px;
  margin-bottom: 15px;
}

.reading-stats {
  background: var(--bg-secondary);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  color: var(--role-primary);
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.definition-tooltip {
  position: fixed;
  background: rgba(0,0,0,0.9);
  color: white;
  padding: 15px;
  border-radius: 10px;
  max-width: 250px;
  z-index: 1000;
  box-shadow: 0 8px 16px rgba(0,0,0,0.3);
}

.tooltip-content h6 {
  color: white;
  margin-bottom: 8px;
}

.tooltip-content p {
  color: rgba(255,255,255,0.9);
  margin: 0;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .story-content {
    padding: 20px 15px;
  }
  
  .story-page {
    padding: 20px;
  }
  
  .page-illustration {
    font-size: 60px;
  }
  
  .page-text {
    font-size: 16px;
  }
  
  .reader-header {
    padding: 10px 15px;
  }
  
  .reading-progress-mini {
    display: none;
  }
  
  .story-navigation {
    padding: 15px;
  }
  
  .vocabulary-grid {
    grid-template-columns: 1fr;
  }
}
</style> 